const isOpenOffer = (offer) => {
  const inventoryType = (offer?.inventoryType || '').toLowerCase()
  const name = (offer?.name || '').toLowerCase()
  return inventoryType === 'open' || name === 'open'
}

function addNodeSeats(node, regular, ga) {
  if (!node || typeof node !== 'object') return
  ;(Array.isArray(node.seats) ? node.seats : []).forEach((id) =>
    regular.add(id)
  )
  Object.entries(node.gaSeats || {}).forEach(([sectionId, seats]) => {
    if (!ga[sectionId]) ga[sectionId] = new Set()
    ;(Array.isArray(seats) ? seats : []).forEach((id) => ga[sectionId].add(id))
  })
  Object.values(node.categories || {}).forEach((category) =>
    addNodeSeats(category, regular, ga)
  )
}

export function collectHeldAndKilledSeatIds(holds, kills) {
  const regular = new Set()
  const ga = {}
  ;[holds, kills].forEach((inventoryMap) => {
    Object.values(inventoryMap || {}).forEach((item) =>
      addNodeSeats(item, regular, ga)
    )
  })
  return { regular, ga }
}

function addSeatId(set, id) {
  if (id == null || id === '') return
  set.add(String(id))
}

/** Every held, killed, or otherwise blocked id, whichever list it was stored in. */
export function blockedSeatIdSet(blocked) {
  const ids = new Set()
  const add = (id) => addSeatId(ids, id)
  if (blocked?.regular?.forEach) blocked.regular.forEach(add)
  Object.values(blocked?.ga || {}).forEach((sectionIds) => {
    if (sectionIds?.forEach) sectionIds.forEach(add)
  })
  if (blocked?.ids?.forEach) blocked.ids.forEach(add)
  return ids
}

export function omitBlockedSeats(seats, gaSeats, blocked) {
  const blockedIds = blockedSeatIdSet(blocked)
  const keep = (id) => !blockedIds.has(String(id))
  return {
    seats: (Array.isArray(seats) ? seats : []).filter(keep),
    gaSeats: Object.fromEntries(
      Object.entries(gaSeats || {}).map(([sectionId, ids]) => [
        sectionId,
        (Array.isArray(ids) ? ids : []).filter(keep),
      ])
    ),
  }
}

const UNAVAILABLE_TICKET_STATUSES = new Set([
  'sold',
  'reserved',
  'reservedForGroupPurchase',
  'pendingTransfer',
  'resaleAvailable',
])

export function unavailableTicketSeatIds(tickets) {
  const ids = new Set()
  ;(Array.isArray(tickets) ? tickets : []).forEach((ticket) => {
    if (!UNAVAILABLE_TICKET_STATUSES.has(ticket?.on_sale_status)) return
    addSeatId(ids, ticket.seatId)
  })
  return ids
}

export function collectInventorySeatIds(nodes) {
  const ids = new Set()
  const walk = (node) => {
    if (!node || typeof node !== 'object') return
    ;(Array.isArray(node.seats) ? node.seats : []).forEach((id) =>
      addSeatId(ids, id)
    )
    Object.values(node.gaSeats || {}).forEach((list) => {
      ;(Array.isArray(list) ? list : []).forEach((id) => addSeatId(ids, id))
    })
    Object.values(node.categories || {}).forEach(walk)
    ;(Array.isArray(node.subOffers) ? node.subOffers : []).forEach(walk)
  }
  const list = Array.isArray(nodes) ? nodes : Object.values(nodes || {})
  list.forEach(walk)
  return ids
}

export function inventoryHasBeenSaved(inventory) {
  if (!inventory || typeof inventory !== 'object') return false
  if (Array.isArray(inventory.removedSpots) && inventory.removedSpots.length) {
    return true
  }
  return ['offers', 'holds', 'kills'].some((key) => {
    const bucket = inventory[key]
    return (
      bucket && typeof bucket === 'object' && Object.keys(bucket).length > 0
    )
  })
}

/**
 * A price-level spot must not be written back onto open when it is held,
 * killed, sold, already removed, or was already absent from saved inventory.
 * GA holds store the id under gaSeats, while the price level may also list
 * that same id in seats. Either list is enough to keep it off open.
 */
export function seatKeptOffOpen(
  seatId,
  { blockedIds, savedPriceLevelIds, savedInventoryIds, inventoryWasSaved }
) {
  const id = String(seatId)
  if (blockedIds?.has(id)) return true
  if (!inventoryWasSaved) return false
  return Boolean(savedPriceLevelIds?.has(id) && !savedInventoryIds?.has(id))
}

function stripBlockedSeatsFromOffers(offers, blocked) {
  return Object.fromEntries(
    Object.entries(offers || {}).map(([key, offer]) => {
      const stripped = omitBlockedSeats(offer?.seats, offer?.gaSeats, blocked)
      const next = { ...offer, ...stripped }
      if (isOpenOffer(offer) && Array.isArray(offer?.subOffers)) {
        next.subOffers = offer.subOffers.map((subOffer) => ({
          ...subOffer,
          ...omitBlockedSeats(subOffer?.seats, subOffer?.gaSeats, blocked),
        }))
      }
      return [key, next]
    })
  )
}

/** Removes seats just assigned to a hold or kill from every offer. */
export function stripSeatsFromOffers(
  offers,
  seatIds = [],
  gaSeatsBySection = {}
) {
  return stripBlockedSeatsFromOffers(offers, {
    regular: new Set(seatIds),
    ga: Object.fromEntries(
      Object.entries(gaSeatsBySection || {}).map(([sectionId, ids]) => [
        sectionId,
        new Set(Array.isArray(ids) ? ids : []),
      ])
    ),
  })
}

/** Spot ids already removed by a sale or assignment. Price levels still list them. */
export function removedInventorySpotIds(removedSpots) {
  const ids = new Set()
  ;(Array.isArray(removedSpots) ? removedSpots : []).forEach((removal) => {
    const spotId =
      removal?.spotId ?? (typeof removal === 'string' ? removal : null)
    addSeatId(ids, spotId)
  })
  return ids
}

/** Removes every seat that is on a hold or kill from every offer. */
export function stripHeldAndKilledSeatsFromOffers(offers, holds, kills) {
  return stripBlockedSeatsFromOffers(
    offers,
    collectHeldAndKilledSeatIds(holds, kills)
  )
}
